import {
  useRef,
  useState,
  useEffect,
  KeyboardEventHandler,
  ChangeEventHandler,
  CSSProperties,
} from 'react';
import {
  Box,
  Divider,
  formLabelClasses,
  inputBaseClasses,
  inputLabelClasses,
  Popper,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { AiIcon } from '#features/common/components/icons/AiIcon';
import { PreferenceKey, useGlobalPreference } from '#features/preferences';
import { scaleConstant } from '#features/theme/helpers';
import { withSubscribe } from '#lib/withSubscribe';
import { getHotkeyPreferenceDisplayLabel } from '#platform/hotkeys/mappers';
import { setSearch, useSearch, useSearchResults } from '../../state';
import { SearchResult, SearchResultCategory, SearchResults } from '../../types';
import { Footer } from '../shared/footer/Footer';
import { toolbarGradient } from '../shared/gradients';
import { SearchDialogBody } from '../shared/search-dialog-body/SearchDialogBody';
import { searchDialogBodyClasses } from '../shared/search-dialog-body/searchDialogBodyClasses';

export type SearchProps = Pick<
  SearchComponentProps,
  'anchorEl' | 'closeOnClickAway' | 'onClick' | 'sx'
>;

export const Search = withSubscribe((props: SearchProps) => {
  const { onClick, ...rest } = props;
  const results = useSearchResults();
  const search = useSearch();

  const handleClick = (result: SearchResult) => {
    setSearch('');
    onClick?.(result);
  };

  return (
    <SearchComponent
      {...rest}
      onClick={handleClick}
      results={results}
      search={search}
      onSearch={setSearch}
    />
  );
});

type SearchComponentProps = {
  anchorEl?: HTMLElement | null;
  search: string;
  onSearch?: (search: string) => void;
  results?: SearchResults;
  closeOnClickAway?: boolean;
  onClick?: (result: SearchResult) => void;
  sx?: CSSProperties;
};

const maxResultsHeight = 300;

const StyledBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.ifx.spacing['spacing-xs'],
    theme.ifx.spacing['spacing-xs'],
    theme.ifx.spacing['spacing-md'],
  ),
}));

const StyledFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(
    theme.ifx.spacing['spacing-md'],
    theme.ifx.spacing['spacing-xs'],
    theme.ifx.spacing['spacing-xxs'],
    theme.ifx.spacing['spacing-xxs'],
  ),
}));

const getNextIndex = (resultsCount: number, currentIndex?: number) => {
  if (typeof currentIndex !== 'number') {
    return 0;
  }
  return (currentIndex + 1) % resultsCount;
};

const getPreviousIndex = (resultsCount: number, currentIndex: number = 0) => {
  if (currentIndex === 0) {
    return resultsCount - 1;
  } else {
    return currentIndex - 1;
  }
};

export const SearchComponent = (props: SearchComponentProps) => {
  const {
    anchorEl,
    results: resultsProp,
    onSearch,
    onClick,
    search,
    closeOnClickAway,
    sx,
  } = props;

  const ghostBodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textfieldRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const results = resultsProp?.results || [];
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState<undefined | number>();
  const theme = useTheme();
  const useHotkeysPreference = useGlobalPreference(PreferenceKey.useHotkeys);
  const hotkeySmartBarFocusPreference = useGlobalPreference(
    PreferenceKey.hotkeySmartBarFocus,
  );

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'ArrowDown') {
      setActiveIndex((prev) => {
        return getNextIndex(results.length, prev);
      });
      event.preventDefault(); // prevent cursor from moving to end of text input
    } else if (event.key === 'ArrowUp') {
      setActiveIndex((prev) => getPreviousIndex(results.length, prev));
      event.preventDefault(); // prevent cursor from moving to start of text input
    } else if (
      event.key === 'Enter' &&
      typeof activeIndex !== 'undefined' &&
      results[activeIndex] &&
      !results[activeIndex].disabled
    ) {
      handleResultClick(results[activeIndex]);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setIsExpanded(false);
    onClick?.(result);
    setActiveIndex(undefined);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onSearch?.(event.target.value);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleClickAway = () => {
    onSearch?.('');
    setIsExpanded(false);
    setActiveIndex(undefined);
  };

  // blur on escape
  useEffect(() => {
    const keydownListener = async (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'escape') {
        inputRef.current?.blur();
        onSearch?.('');
        setIsExpanded(false);
        setActiveIndex(undefined);
      }
    };

    document.addEventListener('keydown', keydownListener);

    return () => document.removeEventListener('keydown', keydownListener);
  }, [onSearch]);

  // scroll active result into view
  useEffect(() => {
    if (bodyRef.current && typeof activeIndex !== 'undefined') {
      const rows =
        bodyRef.current.querySelectorAll(`.${searchDialogBodyClasses.row}`) ||
        [];
      const activeResult = rows[activeIndex];
      activeResult?.scrollIntoView(false);
    }
  }, [activeIndex]);

  const anchor = anchorEl || textfieldRef.current;

  const searchDialogTitle =
    resultsProp?.category === SearchResultCategory.Matches
      ? undefined
      : 'Suggestions';

  const aiGradient = `linear-gradient(90deg, ${
    theme.ifx.palette[
      'Universal + Components/Utility/Purple/utility-purple-500'
    ]
  }, ${
    theme.ifx.palette['Universal + Components/Utility/Blue/utility-blue-500']
  })`;

  return (
    <Box sx={{ position: 'relative', zIndex: 2 }}>
      {isExpanded && closeOnClickAway && (
        <Box
          onClick={handleClickAway}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1,
          }}
        />
      )}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <TextField
          ref={textfieldRef}
          onChange={handleChange}
          onFocus={handleFocus}
          value={search}
          inputRef={inputRef}
          autoComplete="off"
          slotProps={{
            htmlInput: {
              maxLength: 200,
            },
            input: {
              startAdornment: !search && (
                <Typography
                  variant="Text sm/Regular"
                  data-testid="search-placeholder"
                  sx={(theme) => ({
                    color: isExpanded
                      ? theme.ifx.palette['Colors/Text/text-placeholder']
                      : theme.ifx.palette['Colors/Text/text-secondary'],
                    marginBlockEnd: 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                    left: theme.ifx.spacing['spacing-md'],
                    bottom: isExpanded ? 'none' : '40%',
                  })}
                  noWrap
                >
                  {isExpanded
                    ? 'Search or browse Instinct FX'
                    : 'What do you need?'}
                </Typography>
              ),
              endAdornment: !isExpanded && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {useHotkeysPreference && (
                    <Typography
                      variant="Text xs/Regular"
                      sx={(theme) => ({
                        color: theme.ifx.palette['Colors/Text/text-quaternary'],
                        marginBlockEnd: 0,
                        position: 'absolute',
                        left: theme.ifx.spacing['spacing-md'],
                        top: '55%',
                        pointerEvents: 'none',
                      })}
                      noWrap
                    >
                      {getHotkeyPreferenceDisplayLabel(
                        hotkeySmartBarFocusPreference,
                      )}
                    </Typography>
                  )}
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      pl: theme.ifx.spacing['spacing-xxs'],
                    })}
                  >
                    <AiIcon />
                  </Box>
                </Box>
              ),
            },
          }}
          sx={(theme) => ({
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            p: `${theme.ifx.spacing['spacing-md']}px ${theme.ifx.spacing['spacing-md']}px ${theme.ifx.spacing['spacing-xxs']}px ${theme.ifx.spacing['spacing-md']}px`,
            width: isExpanded
              ? theme.ifx.widths['width-xl']
              : theme.ifx.widths['width-sm'],
            backgroundColor: isExpanded
              ? theme.ifx.palette[
                  'Universal + Components/Components/Toolbar/toolbar-bg-secondary'
                ]
              : 'transparent',
            // root
            [`& .${inputBaseClasses.root}`]: {
              height: scaleConstant(32, theme.currentModes.density),
              backgroundColor: 'transparent',
              width: 'calc(100 % +1px)',
              borderRadius: theme.ifx.radius['radius-xs'],
              position: 'relative',
              bottom: 1,
              right: 1,
              m: 0,

              [`&.${inputBaseClasses.focused},  &.${inputBaseClasses.error}, &:not(.${inputBaseClasses.disabled}):not(.${inputBaseClasses.readOnly}):hover, &:not(.${inputBaseClasses.disabled}):not(.${inputBaseClasses.readOnly}).${inputBaseClasses.focused}`]:
                {
                  borderColor: isExpanded
                    ? aiGradient
                    : theme.ifx.palette['Colors/Border/border-hover'],
                },
            },

            // label
            [`& .${formLabelClasses.root}`]: {
              ...theme.typography['Text xs/Regular'],
              marginBlockEnd: 0,
              marginLeft: 5,
              top: 5,
              color: theme.ifx.palette['Colors/Text/text-tertiary'],

              [`&.${inputLabelClasses.focused}:not(.Mui-error)`]: {
                color: theme.ifx.palette['Colors/Text/text-tertiary'],
              },
            },

            // input
            [`& .${inputBaseClasses.input}`]: {
              ...theme.typography['Text md/Medium'],
              marginBlockEnd: 0,
            },

            ...sx,
          })}
          onKeyDown={handleKeyDown}
        />
        <Popper
          open={isExpanded}
          anchorEl={anchor}
          sx={(theme) => ({
            width: theme.ifx.widths['width-xl'],
            zIndex: 3,
            backgroundColor:
              theme.ifx.palette[
                'Universal + Components/Components/Toolbar/toolbar-bg-secondary'
              ],

            border: 'none',

            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '0 1px 1px 1px',
              borderRadius: `${theme.ifx.radius['radius-none']}px ${theme.ifx.radius['radius-none']}px ${theme.ifx.radius['radius-xs']}px ${theme.ifx.radius['radius-xs']}px`,
              background: `linear-gradient(${theme.ifx.palette['Colors/Border/border-framework']} 0%, transparent 40%), ${toolbarGradient}`,
              backgroundSize: '500%',

              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',

              pointerEvents: 'none',
            },
          })}
        >
          <StyledBody ref={bodyRef}>
            <SearchDialogBody
              title={searchDialogTitle}
              results={results}
              activeIndex={activeIndex}
              onClick={handleResultClick}
              sx={{
                overflow: 'auto',
                maxHeight:
                  maxResultsHeight - (footerRef.current?.offsetHeight || 0),
              }}
            />
            <StyledFooter>
              <Divider />
              <Footer />
            </StyledFooter>
          </StyledBody>
        </Popper>
        <StyledBody
          sx={{ visibility: 'hidden', position: 'absolute' }}
          ref={ghostBodyRef}
        >
          {isExpanded && (
            <>
              <SearchDialogBody title={searchDialogTitle} results={results} />
              <StyledFooter ref={footerRef}>
                <Divider />
                <Footer />
              </StyledFooter>
            </>
          )}
        </StyledBody>
      </Box>
    </Box>
  );
};
